import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  MessageSquare,
  UserPlus,
  CheckCircle2,
  AtSign,
  X,
} from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';

interface Notification {
  id: number;
  userId: number;
  type: string;
  title: string;
  message: string;
  entityType?: string;
  entityId?: number;
  isRead: boolean;
  isEmailSent: boolean;
  actionUrl?: string;
  createdBy?: number;
  createdAt: string;
  readAt?: string;
}

interface NotificationsResponse {
  success: boolean;
  data: Notification[];
}

export const NotificationCenter: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  // Fetch notifications
  const { data: notificationsResponse, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiRequest<NotificationsResponse>('GET', '/api/notifications?limit=50'),
    refetchInterval: open ? 30000 : false, // Refetch every 30s when open
  });

  const notifications = notificationsResponse?.data || [];
  // Count unread notifications
  const unreadCount = notifications.filter((n: Notification) => !n.isRead).length || 0;

  // Mark notification as read
  const markAsReadMutation = useMutation({
    mutationFn: (id: number) => apiRequest('PUT', `/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read
  const {
    mutate: markAllAsRead,
    isPending: markAllPending,
  } = useMutation({
    mutationFn: () => apiRequest('PUT', '/api/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Set up WebSocket connection for real-time notifications
  useEffect(() => {
    // Only connect if user is authenticated
    if (!user?.id) {
      console.log('[NotificationCenter] No authenticated user, skipping WebSocket connection');
      return;
    }

    const socketUrl = window.location.origin;

    console.log('[NotificationCenter] Connecting to WebSocket at:', socketUrl);

    const socketInstance = io(socketUrl, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketInstance.on('connect', () => {
      console.log('[NotificationCenter] Connected to notification service');
      setIsConnected(true);

      // Clear any reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Authenticate with the server using actual user ID
      console.log('[NotificationCenter] Authenticating with user ID:', user.id);
      socketInstance.emit('authenticate', { userId: user.id });
    });

    socketInstance.on('authenticated', (data) => {
      console.log('[NotificationCenter] Successfully authenticated:', data);
      // Refresh notifications after authentication
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });

    // Handle generic notifications
    socketInstance.on('notification', (notification) => {
      console.log('[NotificationCenter] Received notification:', notification);

      // Show toast for new notification with longer duration
      toast({
        title: notification.title || 'New Notification',
        description: notification.message || 'You have a new notification',
        duration: 5000, // Show for 5 seconds
        action: notification.actionUrl ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (notification.actionUrl) {
                window.location.href = notification.actionUrl;
              }
            }}
          >
            View
          </Button>
        ) : undefined,
      });

      // Refresh notifications
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });

    // Handle task assignment notifications
    socketInstance.on('task_assigned', (data) => {
      console.log('[NotificationCenter] Task assigned:', data);

      toast({
        title: '📋 New Task Assigned',
        description: `${data.assignedBy} assigned you: "${data.task?.title || 'New task'}"`,
        duration: 6000,
        action: data.actionUrl ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              window.location.href = data.actionUrl;
            }}
          >
            View Task
          </Button>
        ) : undefined,
      });

      // Refresh notifications and tasks
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    });

    // Handle task update notifications
    socketInstance.on('task_updated', (data) => {
      console.log('[NotificationCenter] Task updated:', data);

      const statusEmoji = data.newStatus === 'completed' ? '✅' : data.newStatus === 'in_progress' ? '🔄' : '📝';

      toast({
        title: `${statusEmoji} Task Updated`,
        description: `Task "${data.task?.title}" status changed to ${data.newStatus}`,
        duration: 4000,
        action: data.actionUrl ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              window.location.href = data.actionUrl;
            }}
          >
            View
          </Button>
        ) : undefined,
      });

      // Refresh notifications and tasks
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    });

    // Handle task comment notifications
    socketInstance.on('task_comment', (data) => {
      console.log('[NotificationCenter] Task comment:', data);

      toast({
        title: '💬 New Comment',
        description: `${data.commenter} commented on your task`,
        duration: 4000,
        action: data.actionUrl ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              window.location.href = data.actionUrl;
            }}
          >
            View
          </Button>
        ) : undefined,
      });

      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    });

    // Handle mention notifications
    socketInstance.on('mention:new', (mention) => {
      console.log('[NotificationCenter] New mention:', mention);

      toast({
        title: '@ You were mentioned',
        description: `${mention.mentionedBy} mentioned you in a comment`,
        duration: 5000,
        action: mention.actionUrl ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              window.location.href = mention.actionUrl;
            }}
          >
            View
          </Button>
        ) : undefined,
      });

      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });

    socketInstance.on('task:update', (update) => {
      console.log('[NotificationCenter] Task update event:', update);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('[NotificationCenter] Disconnected from notification service:', reason);
      setIsConnected(false);

      // Attempt reconnection if not intentional disconnect
      if (reason === 'io server disconnect' || reason === 'transport close') {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[NotificationCenter] Attempting to reconnect...');
          socketInstance.connect();
        }, 3000);
      }
    });

    socketInstance.on('connect_error', (error) => {
      console.error('[NotificationCenter] Connection error:', error);
      setIsConnected(false);
    });

    setSocket(socketInstance);

    return () => {
      console.log('[NotificationCenter] Cleaning up WebSocket connection');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      socketInstance.disconnect();
    };
  }, [user?.id, toast, queryClient]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'mention':
        return <AtSign className="h-4 w-4" />;
      case 'task_assigned':
        return <UserPlus className="h-4 w-4" />;
      case 'task_completed':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'comment':
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.id);
    }

    // Navigate to action URL if provided
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
      setOpen(false);
    }
  };

  // Removed auto-dismiss logic that was marking all as read after 300ms
  // Users should explicitly mark notifications as read or click them

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <Badge variant="secondary">{unreadCount} new</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllAsRead()}
                disabled={markAllPending}
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Mark all read
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-[400px]">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-gray-500">
              Loading notifications...
            </div>
          ) : notifications && notifications.length > 0 ? (
            <div className="divide-y">
              {notifications.map((notification: Notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'p-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors',
                    !notification.isRead && 'bg-blue-50/50 dark:bg-blue-900/10'
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex gap-3">
                    <div className={cn(
                      'mt-1 p-2 rounded-full',
                      !notification.isRead
                        ? 'bg-primary/10 text-primary'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-800'
                    )}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {notification.title}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                    {!notification.isRead && (
                      <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <BellOff className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No notifications</p>
              <p className="text-xs text-gray-400 mt-1">
                You're all caught up!
              </p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

// Notification preferences component
export const NotificationPreferences: React.FC = () => {
  const [preferences, setPreferences] = useState({
    email: true,
    inApp: true,
    mentions: true,
    tasks: true,
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: (prefs: any) => apiRequest('PUT', '/api/users/preferences', prefs),
    onSuccess: () => {
      // Show success message
    },
  });

  const handleToggle = (key: keyof typeof preferences) => {
    const newPreferences = { ...preferences, [key]: !preferences[key] };
    setPreferences(newPreferences);
    updatePreferencesMutation.mutate({ notificationPreferences: newPreferences });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Notification Preferences</h3>
      <div className="space-y-3">
        <label className="flex items-center justify-between">
          <span>Email Notifications</span>
          <input
            type="checkbox"
            checked={preferences.email}
            onChange={() => handleToggle('email')}
            className="toggle"
          />
        </label>
        <label className="flex items-center justify-between">
          <span>In-App Notifications</span>
          <input
            type="checkbox"
            checked={preferences.inApp}
            onChange={() => handleToggle('inApp')}
            className="toggle"
          />
        </label>
        <label className="flex items-center justify-between">
          <span>Mentions</span>
          <input
            type="checkbox"
            checked={preferences.mentions}
            onChange={() => handleToggle('mentions')}
            className="toggle"
          />
        </label>
        <label className="flex items-center justify-between">
          <span>Task Assignments</span>
          <input
            type="checkbox"
            checked={preferences.tasks}
            onChange={() => handleToggle('tasks')}
            className="toggle"
          />
        </label>
      </div>
    </div>
  );
};
